import { Component, ojs, app, payload } from "modular-openscriptjs";
import {
    eParse,
    uniqueId,
    setCurrentLocationCoordinates,
    processStart,
    processEnd,
    cleanDispose,
    empty,
} from "../../helpers";
import { dom, tool } from "../../fotastart";
import { STRINGS } from "../../declarations";
import Spinner from "../Blocks/Spinner";
import SolidIcon from "../Blocks/SolidIcon";
import BsIcon from "../Blocks/BsIcon";
import MapSuggestedAddressView from "../Views/MapSuggestedAddressView";
import MapModal from "../Modals/MapModal";

const h = app("h");
const broker = app("broker");
const $e = app("$e");
const gc = app("gc");
const dc = app("dc");

ojs(Spinner, SolidIcon, BsIcon, MapSuggestedAddressView, MapModal);

export default class AddressPickerForm extends Component {
    holder = null;
    mapId = null;
    map = null;
    ed = payload({});
    marker = null;

    render(...args) {
        return h.empty();
    }

    renderForm(...args) {
        this.mapId = `map-${uniqueId()}`;

        return h.div(
            {
                class: "h-100 w-100 position-relative bg-light", // Full container
                style: "min-height: 80vh;",
            },

            // Map Container (Base Layer)
            h.div({
                id: this.mapId,
                class: "w-100 h-100 position-absolute top-0 start-0 z-0",
            }),

            // Static Center Marker (For dragging)
            h.div(
                {
                    class: "d-none position-absolute top-50 start-50 translate-middle z-1 pe-none",
                    id: "static-marker",
                    style: "margin-top: -20px;", // Offset for pin point
                },
                h.i({
                    class: "fas fa-map-marker-alt fa-3x text-dark drop-shadow",
                }),
            ),

            // Floating Search Card (Top Layer)
            h.div(
                {
                    class: "position-absolute top-0 start-0 active-top-0 w-100 p-3 z-3",
                    style: "max-width: 500px;", // Center on desktop
                },
                h.div(
                    {
                        id: "place-autocomplete-card",
                        class: "card border-0 shadow-lg rounded-4 overflow-hidden",
                    },
                    h.div(
                        { class: "d-flex align-items-center p-2 bg-white" },
                        // Back Button
                        h.button(
                            {
                                class: "btn btn-link text-dark shadow-none",
                                onclick: this.method("closeForm"),
                            },
                            h.i({ class: "fas fa-arrow-left" }),
                        ),

                        h.input({
                            class: "form-control border-0 shadow-none fw-medium",
                            type: "search",
                            id: "map-address-search-input",
                            placeholder: "Search for a location...",
                            style: "height: 48px;",
                            oninput: this.method("suggestPlaces", ["${this}"]),
                        }),

                        // Current Location Button (inside search)
                        h.button(
                            {
                                class: "btn btn-link text-primary shadow-none",
                                title: "Use Current Location",
                                onclick: this.method("setCurrentAddress"),
                            },
                            h.i({ class: "fas fa-location-crosshairs fa-lg" }),
                        ),
                    ),
                    // Suggestions Dropdown
                    h.div({
                        class: "d-flex flex-column bg-white",
                        id: "map-address-suggestions-holder",
                        style: "max-height: 60vh; overflow-y: auto;", // Scrollable suggestions
                    }),
                ),
            ),

            // Floating "Done" Action Button (Bottom Layer)
            h.div(
                {
                    class: "position-absolute bottom-0 start-0 w-100 p-4 z-3 d-flex justify-content-center pe-none", // pe-none for pass-through
                    style: "background: linear-gradient(to top, rgba(255,255,255,0.9) 20%, transparent);",
                },
                h.button(
                    {
                        class: "btn btn-dark btn-lg rounded-pill px-5 shadow-lg fw-bold pe-auto", // pe-auto to re-enable clicks
                        style: "min-width: 200px; pointer-events: auto;", // Explicit fallback
                        onclick: this.method("pickLocation", "${this}"),
                    },
                    "Confirm Location",
                ),
            ),

            // Helper text (Optional, floating)
            h.div(
                {
                    class: "position-absolute top-50 start-50 translate-middleBadge z-0 opacity-50 pe-none",
                },
                // Could be map branding or logo
            ),

            ...args,
        );
    }

    $$map = {
        needs: {
            interface: (ed, event) => {
                this.ed = ed;
                const { message, meta } = eParse(ed);

                // Diagnostic breadcrumb - see the matching ones in
                // VoiceOrderCall.js/VoiceOrderCallClient.js. If this
                // logs, the event chain worked and any remaining
                // "map didn't open" issue is inside showMap()/initMap()
                // below, not the event plumbing.
                console.debug(
                    "[AddressPickerForm] map.needs.interface received",
                    "latitude:", message.latitude,
                    "longitude:", message.longitude,
                    "address:", message.address,
                    "meta:", meta,
                );

                // Skip the "Use Current Location?" confirm dialog (see
                // setCurrentLocationCoordinates() in src/helpers.js - on
                // native platforms with permission not yet granted, it
                // shows a blocking SweetAlert2 prompt before anything
                // else) when this picker was opened for the voice
                // agent's pick_location tool (tagged
                // meta.from === "VOICE_LOCATION_PICK" - see
                // VoiceOrderCall.js's pickLocationOnMap()). That prompt
                // popping up unannounced, mid-voice-call, competes with
                // - and can visually sit on top of / block - the map
                // modal this same event is about to open below, so from
                // the user's side it can look like "the map never
                // opened" when really a permission dialog they weren't
                // expecting did. initMap() already falls back to the
                // last-known location or a fixed default when there's
                // nothing better, so skipping this is safe - the user
                // can still search or drag the pin manually.
                if (!message.latitude && meta?.from !== "VOICE_LOCATION_PICK") {
                    setCurrentLocationCoordinates();
                }

                this.showMap(ed, event);
                this.initMap(ed, event).catch((error) => {
                    console.error(
                        "AddressPickerForm.initMap() failed - the map modal opened but the map itself could not initialize:",
                        error,
                    );
                });

                if (this.ed) {
                    let { message } = eParse(this.ed);

                    if (message.has("address")) {
                        let input = dom.id("map-address-search-input");
                        if (input) {
                            input.value = message.address;
                            this.suggestPlaces(input);
                        }
                    }
                }
            },
        },
    };

    $$location = {
        is: {
            available: () => this.closeForm(),
        },
    };

    async setCurrentAddress() {
        setCurrentLocationCoordinates(true, (latitude, longitude) => {
            if (!this.map) return;
            const center = new google.maps.LatLng(latitude, longitude);

            this.animateMapCenter(this.map, center);
            this.map.setZoom(18);

            dom.empty(dom.id("map-address-suggestions-holder"));
            dom.id("map-address-search-input").value = "";
        });
    }

    async initMap(ed, event) {
        const { Map } = await google.maps.importLibrary("maps");
        const { PinElement } = await google.maps.importLibrary("marker");
        const { message, meta } = eParse(ed);

        let config = {
            zoom: 18,
            disableDefaultUI: true,
            draggable: true,
            mapId: "3e0f626ec3ccfc46",
            gestureHandling: "greedy",
        };

        // Resolve one initial lat/lng, in priority order: a candidate
        // location passed in via the event (message.latitude/longitude -
        // e.g. from a search result), then the device's last-known
        // location (dc.latitude/longitude, set by
        // setCurrentLocationCoordinates()), then a fixed Nairobi fallback
        // (matches DeliveryAddress::position()'s own fallback on the
        // backend) - so this is never null/undefined. Both the map
        // center AND the marker below always use this same value, so
        // they can never disagree. This matters because
        // AdvancedMarkerElement strictly validates its position (unlike
        // Map's center, which just falls back to a default silently) -
        // previously the marker was seeded straight from
        // dc.latitude/longitude regardless of what message had, which
        // threw "not a number" and aborted the rest of initMap()
        // (including the idle listener that positions the marker) any
        // time dc.latitude/longitude weren't populated yet - most
        // visibly the very first time a caller (like the voice agent's
        // pick_location tool) opens this picker in a session where the
        // device location has never been fetched.
        let initialLat = -1.286389;
        let initialLng = 36.817223;

        if (dc.latitude != null && dc.longitude != null) {
            initialLat = dc.latitude;
            initialLng = dc.longitude;
        }

        if (message.latitude != null && message.longitude != null) {
            initialLat = Number(message.latitude);
            initialLng = Number(message.longitude);
        }

        config.center = { lat: initialLat, lng: initialLng };

        let mapDiv = dom.id(this.mapId);

        this.map = new Map(mapDiv, config);

        // Custom User Avatar Pin
        const glyphImg = h.img({
            class: "rounded-circle",
            style: "width: 100%; height: 100%; objectFit: contain;",
        });

        if (gc.user.profile_photo_path) {
            glyphImg.src = gc.user.profile_photo_path;
        } else {
            glyphImg.src = "/assets/images/user-avatar.png";
        }

        const pinBackground = new PinElement({
            borderColor: "#8f3985",
            background: "white",
            glyphColor: "white",
            glyph: glyphImg,
            scale: 2.0,
        });

        this.marker = new google.maps.marker.AdvancedMarkerElement({
            map: this.map,
            position: { lat: initialLat, lng: initialLng },
            content: pinBackground.element,
            title: `${gc.user?.name ?? "You"}`,
            draggable: false, // We move the map, not the marker (mostly)
        });

        if (!message.latitude) {
            this.setCurrentAddress();
        }
		
        let tmpMarker = dom.id("static-marker");

        // Map Interaction Listeners
        this.map.addListener("idle", () => {
            const center = this.map.getCenter();
            if (this.marker) {
                this.marker.position = center;
                this.marker.map = this.map; // Ensure visibility
            }
        });

        this.map.addListener("dragstart", function () {
            // Hide actual marker, show static center marker
            if (this.marker) this.marker.map = null;
            // dom.centerInside(mapDiv, tmpMarker); // CSS handles centering now
            tmpMarker.classList.remove("d-none");
        });

        this.map.addListener("dragend", function () {
            tmpMarker.classList.add("d-none");
            // Marker reappears via idle listener
        });
    }

    async suggestPlaces(input) {
        const { Place, AutocompleteSessionToken, AutocompleteSuggestion } =
            await google.maps.importLibrary("places");

        let value = input.value;
        let holder = dom.id("map-address-suggestions-holder");

        if (empty(value)) {
            dom.empty(holder);
            return;
        }

        if (this.ed) {
            let { message } = eParse(this.ed);

            if (message.has("city")) {
                value += `, ${message.city}`;
            }

            if (message.has("state")) {
                value += `, ${message.state}`;
            }

            if (message.has("country")) {
                value += `, ${message.country}`;
            }
        }

        let request = {
            input: value,
            language: "en-US",
            region: "ke",
        };

        const token = new AutocompleteSessionToken();
        request.sessionToken = token;

        const { suggestions } =
            await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        let i = 0;
        dom.empty(holder);

        for (let suggestion of suggestions) {
            const placePrediction = suggestion.placePrediction;

            h.MapSuggestedAddressView(placePrediction.text.toString(), {
                listeners: {
                    click: () => {
                        this.selectAddress(placePrediction.toPlace());
                    },
                },
                parent: holder,
            });

            if (i > 1) break;
            i++;
        }

        if (empty(input.value)) {
            dom.empty(holder);
            return;
        }
    }

    async selectAddress(place) {
        await place.fetchFields({
            fields: ["displayName", "formattedAddress", "location"],
        });

        let searchField = dom.id("map-address-search-input");
        searchField.value = `${place.displayName}`;

        if (place.viewport) {
            this.map.fitBounds(place.viewport);
            this.marker.position = place.location;
        } else {
            this.animateMapCenter(this.map, place.location);
            this.map.setZoom(18);
        }

        dom.empty(dom.id("map-address-suggestions-holder"));
    }

    async showMap() {
        if (this.holder) {
            cleanDispose(this.holder);
        }

        this.holder = tool.plainModal(
            [
                h.MapModal(this.renderForm()),
                { class: "modal-lg modal-fullscreen p-0" },
            ],
            { data_bs_backdrop: "static", style: "z-index: 1200;" },
        );

        this.holder.show();
    }

    closeForm() {
        this.holder.hide();
    }

    async pickLocation(btn) {
        if (btn) {
            dom.disable(btn);
            h.Spinner({ parent: btn, resetParent: true });
        }

        processStart({ title: "Extracting Address Info..." });

        let geocoder = new google.maps.Geocoder();

        const latLng = this.marker.position;

        // Perform reverse geocoding
        geocoder.geocode({ location: latLng }, (results, status) => {
            if (status === "OK") {
                if (results[0]) {
                    const placeInfo = results[0];

                    let p = {
                        address: placeInfo.formatted_address,
                        latitude: `${this.marker.position.lat}`,
                        longitude: `${this.marker.position.lng}`,
                    };

                    let m = {};

                    if (this.ed) {
                        let { meta } = eParse(this.ed);
                        m = meta;
                    }

                    broker.send($e.map.has.location, payload(p, m));
                } else {
                    if (btn) {
                        dom.enable(btn);
                        dom.put("Done", btn);
                    }

                    inform("Could not find location", STRINGS.ERROR);
                }
            }

            processEnd();
        });
    }

    easeInOutQuad(t) {
        return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    }

    animateMapCenter(map, newCenter, duration = 1000) {
        const currentCenter = map.getCenter();
        const latStart = currentCenter.lat();
        const lngStart = currentCenter.lng();
        const latEnd = newCenter.lat();
        const lngEnd = newCenter.lng();

        const frameRate = 60;
        const frames = Math.round((duration / 1000) * frameRate);
        let frame = 0;

        const animate = () => {
            frame++;
            const progress = this.easeInOutQuad(frame / frames);

            const lat = latStart + (latEnd - latStart) * progress;
            const lng = lngStart + (lngEnd - lngStart) * progress;

            map.setCenter({ lat: lat, lng: lng });

            if (frame < frames) {
                requestAnimationFrame(animate);
            }
        };

        animate();
    }
}
